import { Eye, EyeOff, LogIn } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  // Load remembered email and password
  useEffect(() => {
    const remembered = localStorage.getItem('login_remember')
    if (remembered === 'true') {
      setEmail(localStorage.getItem('login_email') || '')
      setPassword(localStorage.getItem('login_password') || '')
      setRememberMe(true)
    }
  }, [])

  const { login } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      console.log('📝 Login form submitted')
      await login(email, password)
      console.log('✅ Login successful')

      // Save credentials if remember me is checked
      if (rememberMe) {
        localStorage.setItem('login_email', email)
        localStorage.setItem('login_password', password)
        localStorage.setItem('login_remember', 'true')
      } else {
        localStorage.removeItem('login_email')
        localStorage.removeItem('login_password')
        localStorage.removeItem('login_remember')
      }

      // returnUrl varsa oraya git, yoksa dashboard'a git
      const returnUrl = searchParams.get('returnUrl')
      navigate(returnUrl || '/dashboard', { replace: true })
    } catch (err: any) {
      console.error('❌ Login error:', err)
      const errorMsg = err.response?.data?.detail || err.message || (typeof err === 'string' ? err : 'Login failed')
      setError(errorMsg as string)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <div style={{
        background: '#ffffff',
        padding: '48px',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '440px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Logo/Icon */}
        <div style={{
          width: '64px',
          height: '64px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px'
        }}>
          <LogIn size={32} color="white" />
        </div>

        <h1 style={{
          color: '#1a202c',
          marginBottom: '8px',
          fontSize: '32px',
          fontWeight: '700'
        }}>
          Welcome Back
        </h1>
        <p style={{
          color: '#718096',
          marginBottom: '32px',
          fontSize: '16px'
        }}>
          Login to RunAI.live
        </p>

        {error && (
          <div style={{
            background: '#fee',
            color: '#c53030',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '24px',
            border: '1px solid #fc8181',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit(e);
        }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: '#2d3748',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="johndoe@mail.com"
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                background: '#f7fafc',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                color: '#2d3748',
                fontSize: '15px',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              color: '#2d3748',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 16px',
                  paddingRight: '44px',
                  background: '#f7fafc',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  color: '#2d3748',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#667eea'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#718096',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px'
                }}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer'
              }}
            />
            <label htmlFor="rememberMe" style={{
              color: '#718096',
              fontSize: '14px',
              cursor: 'pointer'
            }}>
              Remember me
            </label>
            <style>{`
              #rememberMe {
                accent-color: #667eea;
              }
            `}</style>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: loading ? '#a0aec0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: loading ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.4)'
            }}
            onMouseEnter={(e) => {
              if (!loading) {e.currentTarget.style.transform = 'translateY(-2px)'}
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
            }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <p style={{
          color: '#718096',
          marginTop: '24px',
          textAlign: 'center',
          fontSize: '14px'
        }}>
          Don&apos;t have an account?{' '}
          <Link
            to="/register"
            style={{
              color: '#667eea',
              fontWeight: '600',
              textDecoration: 'none'
            }}
          >
            Register
          </Link>
        </p>
      </div>
    </div>
  )
}
